import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import PDStartupHelp, { PDStartupHelpIcon } from "./PDStartupHelp";
import PDBuySubscription from "./PDBuySubscription";

import useFetch from "../../hooks/useFetch";
import html2canvas from "html2canvas";
import logo from '../../assets/logo128x128.png';
import QRCode from 'react-qr-code';
import { motion, AnimatePresence } from "framer-motion";
import { Radar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
} from "chart.js";

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const PDFounders = ({isActiveSub, startDate, endDate}) => {
    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();
    const userId = authState?.user?._id;
    const userCode = authState?.user?.think5whyscode;
    const userName =  authState?.user?.userName;
    const [questions, setQuestions] = useState([]);
    const [categoryMapping, setCategoryMapping] = useState([]);
    const [scores, setScores] = useState(Array(20).fill(50));
    const [history, setHistory] = useState([]);
    const [currentResult, setCurrentResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    const [showPDSubscription, setShowPDSubscription] = useState(false);

    const helpRef = useRef();

    const shareCardRef = useRef(null);

    const categories = ["Discipline", "Execution", "Learning", "Risk", "Consistency"];

    useEffect(() => {
        const generateQR = async () => {
        if (!currentResult) return;

        const qrText = `Certificate of Authenticity for ${userName || 'User'} ${userId || ''}. ${currentResult?.total || 0}/2000`;

        try {
            const url = await QRCode.toDataURL(qrText, {
            width: 180,
            margin: 1,
            color: {
                dark: '#0f172a',   // Dark color
                light: '#ffffff'   // Background
            }
            });
            setQrCodeUrl(url);
        } catch (err) {
            console.error("QR Code generation failed", err);
        }
        };

        generateQR();
    }, [currentResult, userName, userId]);
    

    const fetchQuestions = async () => {
        try {
            const config = {
                url: "/pd/assessment/questions?setId=1",
                method: "get"
            };

            const res = await fetchData(config);
            if (res?.questions) {
                setQuestions(res.questions);
                setCategoryMapping(res.categoryMapping || Array(20).fill(0));
            }
        } catch (err) {
            console.error("Questions fetch failed:", err);
        }
    };

    const fetchHistory = async () => {
        if (!authState?.user?._id) return;

        try {
            const config = {
                url: "/pd/assessment/my",
                method: "post",
                data: { userId: authState.user._id },
                headers: { Authorization: authState.token }
            };

            const res = await fetchData(config, { silent: true });
            setHistory(res?.assessment?.sort((a, b) => a.week - b.week) || []);
        } catch (err) {
            console.error("History fetch failed:", err);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            const payload = {
                userResponse: scores.join(","),
                week: Math.max(1, history.length + 1),
                setId: "1"
            };

            const config = {
                url: "/pd/assessment/createassessment",
                method: "post",
                data: payload,
                headers: { Authorization: authState.token }
            };

            const res = await fetchData(config);

            if (res?.status) {
                await fetchHistory();
                setCurrentResult({
                    total: totalScore,
                    archetype: res.assessment.archetype,
                    week: res.assessment.week,
                    insight: generateInsight()
                });
            } else {
                setError(res?.message || "Failed to save assessment");
            }
        } catch (err) {
            console.error("Submit error:", err);
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const loadInitialData = async () => {
        setLoading(true);
        setError(null);
        try {
            await fetchQuestions();
            await fetchHistory();       
        } catch (err) {
            console.error("Initial load error:", err);
            setError("Failed to load data. Please refresh.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadInitialData();
    }, []);

    const getCategoryScores = () => {
        const catScores = [0, 0, 0, 0, 0];
        const catCount = [0, 0, 0, 0, 0];

        scores.forEach((score, i) => {
            const cat = categoryMapping[i] || 0;
            catScores[cat] += score;
            catCount[cat]++;
        });

        return catScores.map((sum, i) => (catCount[i] ? Math.round(sum / catCount[i]) : 50));
    };

    const categoryScores = getCategoryScores();
    const totalScore = scores.reduce((a, b) => a + b, 0);

    const radarChartData = {
        labels: categories,
        datasets: [{
            label: "Your Founder Score",
            data: categoryScores,
            backgroundColor: "rgba(34, 211, 238, 0.35)",
            borderColor: "#22d3ee",
            borderWidth: 3,
            pointBackgroundColor: "#67e8f9",
            pointBorderColor: "#fff"
        }]
    };

    const radarOptions = {
        scales: {
            r: {
                angleLines: { color: "#374151" },
                grid: { color: "#374151" },
                pointLabels: { color: "#e5e7eb", font: { size: 12 } },
                ticks: { color: "#9ca3af", font: { size: 10 }, stepSize: 20 },
                min: 0,
                max: 100
            }
        },
        plugins: { legend: { display: false }, tooltip: { backgroundColor: "#1f2937" } },
        maintainAspectRatio: false
    };

    const generateInsight = () => {
        const avg = totalScore / 20;
        const strongestIndex = categoryScores.indexOf(Math.max(...categoryScores));
        const strongestCat = categories[strongestIndex];

        if (avg > 75) return `Strong growth mindset! Your ${strongestCat} stands out.`;
        if (avg > 55) return `Good base. Focus on ${strongestCat} and treat failures as learning fuel.`;
        return `Start building daily consistency. Growth comes from effort.`;
    };

    const loadWeek = (assessment) => {
        const responses = assessment.userResponse.split(',').map(Number);
        setScores(responses);
        setCurrentResult({
            total: assessment.totalScore,
            archetype: assessment.archetype,
            week: assessment.week,
            insight: generateInsight()
        });
    };

    const generateShareCard = async () => {
        if (!shareCardRef.current || !currentResult) return;

        try {
            const card = shareCardRef.current;
            const originalDisplay = card.style.display;

            card.style.display = "block";
            card.style.position = "absolute";
            card.style.left = "-9999px";

            const canvas = await html2canvas(card, { 
                scale: 2.5,           // Higher quality
                useCORS: true,
                backgroundColor: "#0f172a"
            });

            const link = document.createElement("a");
            link.download = `Think5Whys_FounderScore_Week${currentResult.week}.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();

            card.style.display = originalDisplay;
        } catch (err) {
            console.error("Share card error:", err);
            alert("Failed to generate card");
        }
    };

    if (loading && !questions.length) {
        return <div className="text-center py-20 text-gray-400">Loading assessment...</div>;
    }

    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t border-emerald-400 mt-2">
            {error && (
                <div className="bg-red-500/10 border border-red-500 text-red-400 p-3 rounded-xl mb-4">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <div className="flex justify-between items-start">
                        <div className="bg-[#1f2937] p-2 rounded-3xl border border-gray-800 w-full">
                            <h2 className="text-sm text-cyan-200 font-bold">
                                🚀 Founder Readiness Assessment
                            </h2>
                            <p className="text-gray-200 mt-1 text-xs">
                                Week {history.length + 1} • Rate 0–100
                            </p>
                        </div>
                         <div className="ml-2">
                            <PDStartupHelpIcon onClick={() => helpRef.current.open()} />
                        </div>
                        {/* <button
                            onClick={() => setShowHelp(true)}
                            className="ml-2 w-10 h-10 flex items-center justify-center rounded-xl bg-cyan-500/20 text-cyan-300"
                            title="How this works"
                        >
                            ❓
                        </button> */}
                    </div>
                    <PDStartupHelp ref={helpRef} />
                    {/* <AnimatePresence>
                        {showHelp && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 20 }}
                                className="
                                    fixed lg:absolute
                                    inset-0 lg:inset-auto
                                    lg:top-20 lg:left-1/2 lg:-translate-x-1/2
                                    lg:w-[420px]
                                    bg-black/95 lg:bg-[#111827]
                                    border border-gray-800
                                    rounded-none lg:rounded-2xl
                                    z-50
                                    flex flex-col
                                    overflow-hidden
                                "
                            >
                                <div className="flex justify-between items-center p-3 border-b border-gray-800">
                                    <div className="text-xs text-cyan-300 font-bold">
                                        🚀 How This Works
                                    </div>

                                    <button
                                        onClick={() => setShowHelp(false)}
                                        className="w-10 text-xs text-gray-300 hover:text-red-400"
                                    >
                                        ✕
                                    </button>
                                </div>

                                <div className="p-4 space-y-3 overflow-y-auto">

                                    <div className="bg-[#111827] p-3 rounded-xl border border-gray-800">
                                        <div className="text-cyan-300 text-xs font-bold">
                                            1. Answer Honestly
                                        </div>
                                        <div className="text-gray-300 text-xs">
                                            Rate yourself 0–100 without bias.
                                        </div>
                                    </div>

                                    <div className="bg-[#111827] p-3 rounded-xl border border-gray-800">
                                        <div className="text-cyan-300 text-xs font-bold">
                                            2. System Learns You
                                        </div>
                                        <div className="text-gray-300 text-xs">
                                            AI maps your founder readiness pattern.
                                        </div>
                                    </div>

                                    <div className="bg-[#111827] p-3 rounded-xl border border-gray-800">
                                        <div className="text-cyan-300 text-xs font-bold">
                                            3. Weekly Growth Score
                                        </div>
                                        <div className="text-gray-300 text-xs">
                                            You track evolution, not perfection.
                                        </div>
                                    </div>

                                    <div className="bg-[#111827] p-3 rounded-xl border border-gray-800">
                                        <div className="text-cyan-300 text-xs font-bold">
                                            4. Fix Weak Signals
                                        </div>
                                        <div className="text-gray-300 text-xs">
                                            Focus improves automatically every week.
                                        </div>
                                    </div>

                                </div>

                                <div className="p-3 border-t border-gray-800 text-center">
                                    <div className="text-[10px] text-gray-500">
                                        Swipe mindset → Build → Iterate → Grow
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence> */}

                    <div className="space-y-4 pr-3">
                        {questions.map((q, i) => (
                            <div key={i} className="bg-[#111827] p-2 rounded-2xl border border-gray-800">
                                <div className="flex justify-between mb-2 text-xs">
                                    <span>{i + 1}. {q}</span>
                                    <span className="text-xs font-mono text-cyan-400">{scores[i]}</span>
                                </div>
                                <input
                                    type="range"
                                    min="0" max="100" step="5"
                                    value={scores[i]}
                                    onChange={(e) => {
                                        const newScores = [...scores];
                                        newScores[i] = Number(e.target.value);
                                        setScores(newScores);
                                    }}
                                    className="w-full accent-cyan-400 text-xs"
                                />
                            </div>
                        ))}
                    </div>
                    {!isActiveSub && (
                        <div className="bg-[#111827] border border-pink-300 rounded-2xl p-4 space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="text-2xl">🚀</div>
                                <div>
                                    <p className="text-sm text-pink-200 font-semibold">
                                        DigitalBoutique subscription isn't active
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        You can still explore this section. Subscribe anytime to save your weekly scores and unlock full tracking — no pressure.
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-row gap-3">
                                <button
                                    onClick={() => {}}
                                    className="flex-1 text-center text-xs lg:text-sm font-medium py-1 px-2 w-fit rounded-xl border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 transition-all duration-200"
                                >
                                    Maybe Later
                                </button>

                                <button
                                    onClick={() => {
                                        setShowPDSubscription(true);
                                    }}
                                    className="flex-1 w-fit py-1 px-2 bg-pink-600 hover:bg-pink-500 rounded-lg text-white font-medium transition-colors"
                                    >
                                    Subscribe Now
                                </button>
                            </div>
                        </div>
                    )}
                    {showPDSubscription && (
                        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] px-4 py-6 overflow-y-auto">
                        <div className="bg-gray-900 border border-pink-500/50 rounded-3xl w-full max-w-2xl my-auto max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-end px-4 pt-4">
                            <button
                                onClick={() => {
                                setShowPDSubscription(false);
                                loadInitialData();
                                }}
                                className="text-xs lg:text-sm w-fit px-2 py-1 text-gray-400 hover:text-white leading-none"
                            >
                                ✕
                            </button>
                            </div>
                            <PDBuySubscription user={userId}/>
                        </div>
                        </div>
                    )}
                    <button
                        onClick={handleSubmit}
                        disabled={loading || !isActiveSub}
                        className="w-48 bg-green-500 hover:bg-green-600 py-2 rounded-2xl font-bold text-xs transition disabled:opacity-70"
                    >
                        {loading ? "Saving..." : `Save Week ${history.length + 1}`}
                    </button>
                </div>

                {/* RIGHT: Results */}
                <div className="space-y-4">
                    {currentResult && (
                        <>
                            <div className="bg-[#1f2937] p-6 rounded-3xl border border-gray-800 text-center">
                                <div className="text-lg font-black">{currentResult.total}<span className="text-3xl text-gray-400">/2000</span></div>
                                <div className="text-xs text-cyan-400 mt-2 font-bold">{currentResult.archetype}</div>
                            </div>

                            <div className="bg-[#1f2937] p-5 rounded-3xl border border-gray-800 h-[360px]">
                                <Radar data={radarChartData} options={radarOptions} />
                            </div>

                            <div className="bg-[#1f2937] p-5 rounded-3xl border border-gray-800">
                                <div className="font-bold text-yellow-300 mb-3">🧠 Growth Mindset Insight</div>
                                <p className="text-xs text-gray-300 leading-relaxed">
                                    {currentResult.insight}
                                </p>
                            </div>

                            {/* Hidden Share Card - Updated Design */}
                            <div ref={shareCardRef} style={{ display: "none" }}>
                                <div className="w-[460px] h-[320px] bg-gradient-to-br from-cyan-700 via-blue-800 to-indigo-900 p-8 text-white rounded-3xl flex flex-col justify-between shadow-2xl">
                                    <div className="flex items-center gap-2 px-6 py-1 bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700">
                                        <div>
                                            <img 
                                            src={logo} 
                                            alt="Think5Whys Logo" 
                                            className="w-16 h-16 rounded-xl object-contain"
                                            />
                                        </div>
                                        <div className="flex flex-col justify-center items-center">
                                            <h2 className="text-2xl font-bold text-white tracking-tight">
                                                Think5Whys
                                            </h2>
                                            <p className="flex flex-row justify-center text-slate-400 text-[10px] mt-1">
                                                Founder Skill Score • Week {currentResult?.week} 
                                                <span className="ml-2">{new Date().toLocaleDateString('en-IN')}</span>
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end">
                                            <div className="bg-white p-2 rounded-xl">
                                                <QRCode 
                                                    value={`CERTIFICATE OF AUTHENTICITY
                                                Name: ${userName || 'User'} ${userCode || ''}
                                                Score: ${currentResult?.total || 0}/2000
                                                ID: ${currentResult?.week 
                                                        ? `PD${String(Date.now()).slice(-8)}${currentResult.week}` 
                                                        : "PD-" + Date.now().toString().slice(-10)}
                                                Platform: Think5Whys`}
                                                    size={40}
                                                    bgColor="#ffffff"
                                                    fgColor="#0f172a"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-lg font-black leading-tight mb-2">
                                            {currentResult?.archetype}
                                        </div>
                                        
                                        <div className="text-xl font-black tracking-tighter mb-2">
                                            {currentResult?.total}<span className="text-xl opacity-70">/2000</span>
                                        </div>

                                        <div className="text-sm opacity-90 font-medium">
                                            {authState?.user?.name || "Founder"}
                                        </div>
                                        <div className="text-xs opacity-75 mt-1">
                                            Code: {authState?.user?.think5whyscode || "XXXXXX"}
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-[10px] opacity-60 tracking-widest">
                                            CERTIFICATE ID • {currentResult?.week 
                                                ? `PD${String(Date.now()).slice(-8)}${currentResult.week}` 
                                                : "PD-" + Date.now().toString().slice(-10)}
                                        </div>
                                    </div>
                                    <div className="text-[9px] opacity-60 text-center leading-tight border-t border-white/20 pt-3">
                                        Powered By: Think5Whys Global DeepTech Solutions Private Limited<br />
                                        Mumbai, India | www.think5whys.com
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={generateShareCard}
                                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 py-3.5 rounded-2xl font-bold"
                            >
                                📸 Download Share Card
                            </button>
                        </>
                    )}

                    {/* History */}
                    <div className="bg-[#1f2937] p-5 rounded-3xl border border-gray-800">
                        <div className="font-bold mb-4">📅 90-Day History</div>
                        <div className="grid grid-cols-4 lg:grid-cols-6 gap-1">
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((w) => {
                                const record = history.find((h) => h.week === w);
                                return (
                                    <button
                                        key={w}
                                        onClick={() => record && loadWeek(record)}
                                        disabled={!record}
                                        className={`py-1 px-1 text-[10px] rounded-xl transition-all ${
                                            record ? "bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300" : "bg-gray-800 text-gray-600 cursor-not-allowed"
                                        }`}
                                    >
                                        W{w}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PDFounders;